/**
 * A tab strip that can be moved sideways with a mouse when its tabs do not fit.
 *
 * The owner, 2026-11-02, of Settings on a narrower window: the tab strip "has
 * no way to navigate on smaller width screens", so add arrows left and right,
 * and taking the mouse towards the edge of the strip should scroll it. Every
 * underline strip hides its scrollbar on purpose, and a mouse has no sideways
 * wheel, so until this a tab past the edge could be reached with a keyboard or
 * a finger and not with a mouse at all.
 *
 * What it adds around the strip, and only while the tabs overflow:
 *
 * - an arrow at each end that has hidden tabs beyond it, over a fade, so the
 *   clipped edge reads as "more this way". A press moves most of the visible
 *   width, smoothly. The arrows take no tab stop (`tabIndex={-1}`): a keyboard
 *   already moves through the tabs, and the browser scrolls a focused tab into
 *   view by itself
 * - hover scrolling: a mouse resting in an edge zone (the arrow and its fade)
 *   scrolls the strip that way, faster the closer it is to the edge, and stops
 *   when it leaves the zone, presses a button or reaches the end. Mouse only,
 *   because a finger already drags the strip
 * - the vertical wheel scrolls the strip sideways, and is left to the page
 *   whenever the strip cannot move further that way
 * - the open tab is brought into view when the strip first draws and whenever
 *   the open tab changes, by setting the strip's own `scrollLeft`, so the
 *   window never jumps the way `scrollIntoView` would make it
 *
 * When everything fits nothing is drawn but a plain wrapper, so a wide window
 * looks exactly as it did. The numbers and the arithmetic are in
 * `tab-scroll-model.ts`, unit tested there.
 *
 * WHY A MUTATION OBSERVER. The open tab changes in three different ways across
 * the strips that use this (a route that `NavLink` matches, a `?view=` the
 * caller reads, a tab held in state), and with `NavLink` the strip's parent
 * need not render again when the route moves. Watching the strip's own
 * `aria-current` and `aria-selected` catches all three without asking each
 * caller for a key.
 *
 * requestAnimationFrame never fires in a hidden browser pane. The hover loop
 * only ever asks for the next frame from inside a frame, is cancelled when the
 * pointer leaves and when the strip unmounts, and does nothing at all where
 * there is no requestAnimationFrame, so a pane that never paints just never
 * hover scrolls.
 */
import type { HTMLAttributes, MutableRefObject, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn'
import { IconChevronLeft, IconChevronRight } from './icons'
import {
  EDGE_ZONE_PX,
  NO_EDGES,
  STEP_SETTLE_MS,
  hoverStep,
  hoverVelocity,
  revealScrollLeft,
  sameEdges,
  stepTarget,
  stripEdges,
  wheelScrollDelta,
  type StripEdges,
  type StripMetrics,
} from './tab-scroll-model'

/** Measured before the first paint in a browser, and quietly skipped on the server. */
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect

/** The open tab, whichever way the strip marks it: a link to this page, or a chosen tab. */
const OPEN_TAB = '[aria-current="page"], [aria-selected="true"]'

function metricsOf(strip: HTMLElement): StripMetrics {
  return { scrollLeft: strip.scrollLeft, scrollWidth: strip.scrollWidth, clientWidth: strip.clientWidth }
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function scrollStripTo(strip: HTMLElement, left: number, smooth: boolean): void {
  if (typeof strip.scrollTo === 'function') {
    strip.scrollTo({ left, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' })
  } else {
    strip.scrollLeft = left
  }
}

function openTab(strip: HTMLElement): HTMLElement | null {
  return strip.querySelector<HTMLElement>(OPEN_TAB)
}

/** Scroll the strip, and only the strip, so the open tab is in view clear of the arrows. */
function revealOpenTab(strip: HTMLElement, smooth: boolean): void {
  const tab = openTab(strip)
  if (tab === null) return
  const stripBox = strip.getBoundingClientRect()
  const tabBox = tab.getBoundingClientRect()
  const itemStart = tabBox.left - stripBox.left - strip.clientLeft + strip.scrollLeft
  const target = revealScrollLeft({ ...metricsOf(strip), itemStart, itemEnd: itemStart + tabBox.width })
  if (Math.abs(target - strip.scrollLeft) >= 1) scrollStripTo(strip, target, smooth)
}

/**
 * The hover scroll's frame loop, one per strip.
 *
 * `aim` sets the speed from the latest pointer position and starts the loop if
 * it is not running. Each frame moves the strip by what the time since the
 * last frame is worth, carrying the fraction, and the loop ends itself when
 * the speed is zero or the strip has reached the end it is heading for.
 *
 * Exported for its test, which runs it frame by frame. Nothing else needs it.
 */
export class HoverScroll {
  /** A mouse button is down, so the tab under it must not slide away before the click lands. */
  held = false
  /** An arrow press's smooth scroll is still running until this time. */
  settleUntil = 0
  private velocity = 0
  private frame = 0
  private last = 0
  private carry = 0

  constructor(
    private readonly strip: () => HTMLElement | null,
    private readonly onMove: () => void,
  ) {}

  aim(velocity: number): void {
    this.velocity = velocity
    if (velocity === 0) {
      this.stop()
      return
    }
    if (this.frame === 0 && typeof requestAnimationFrame === 'function') this.frame = requestAnimationFrame(this.tick)
  }

  stop(): void {
    if (this.frame !== 0 && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.frame)
    this.frame = 0
    this.last = 0
    this.carry = 0
    this.velocity = 0
  }

  private readonly tick = (now: number): void => {
    this.frame = 0
    const strip = this.strip()
    if (strip === null || this.velocity === 0) {
      this.stop()
      return
    }
    const frameMs = this.last === 0 ? 0 : now - this.last
    this.last = now
    if (!this.held && now >= this.settleUntil) {
      const edges = stripEdges(metricsOf(strip))
      if ((this.velocity < 0 && !edges.left) || (this.velocity > 0 && !edges.right)) {
        this.stop()
        return
      }
      const { step, carry } = hoverStep(this.velocity, frameMs, this.carry)
      this.carry = carry
      if (step !== 0) {
        strip.scrollLeft += step
        this.onMove()
      }
    }
    this.frame = requestAnimationFrame(this.tick)
  }
}

export interface ScrollableStripProps extends HTMLAttributes<HTMLElement> {
  /** The strip's own element: a `nav` of links, or a `div` tablist. */
  as?: 'nav' | 'div'
  /**
   * Off draws the bare strip with nothing around it, for the tray, which is a
   * segmented switch sized to its content rather than a strip of places.
   */
  scroll?: boolean
  children?: ReactNode
}

/**
 * The strip element itself, with `className` and every other prop passed to it
 * unchanged, inside a wrapper that holds the arrows. The ref is the strip's.
 */
export const ScrollableStrip = forwardRef<HTMLElement, ScrollableStripProps>(function ScrollableStrip(
  { as: Tag = 'nav', scroll = true, className, children, ...rest },
  forwardedRef,
) {
  const stripRef = useRef<HTMLElement | null>(null)
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      stripRef.current = node
      if (typeof forwardedRef === 'function') forwardedRef(node)
      else if (forwardedRef) (forwardedRef as MutableRefObject<HTMLElement | null>).current = node
    },
    [forwardedRef],
  )
  const [edges, setEdges] = useState<StripEdges>(NO_EDGES)
  // A person has moved the strip themselves, so a later resize must not snap
  // it back to the open tab under them.
  const moved = useRef(false)
  const hover = useRef<HoverScroll | null>(null)
  if (hover.current === null) {
    hover.current = new HoverScroll(
      () => stripRef.current,
      () => {
        moved.current = true
      },
    )
  }

  const measure = useCallback(() => {
    const strip = stripRef.current
    if (strip === null) return
    const next = stripEdges(metricsOf(strip))
    setEdges((was) => (sameEdges(was, next) ? was : next))
  }, [])

  useBeforePaint(() => {
    const strip = stripRef.current
    if (!scroll || strip === null) return undefined
    revealOpenTab(strip, false)
    measure()
    let shown = openTab(strip)

    // After a resize, a tab added or removed, or the open tab changing.
    const settle = () => {
      const now = openTab(strip)
      if (now !== shown) {
        shown = now
        revealOpenTab(strip, true)
      } else if (!moved.current) {
        revealOpenTab(strip, false)
      }
      measure()
    }

    const onScroll = () => measure()
    const onWheel = (event: WheelEvent) => {
      const delta = wheelScrollDelta(event, metricsOf(strip))
      if (delta === 0) return
      event.preventDefault()
      moved.current = true
      strip.scrollLeft += delta
    }

    // The strip's own box, and each tab's, since a web font arriving widens
    // the tabs without the strip changing size.
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(settle)
    const observeAll = () => {
      if (resize === null) return
      resize.disconnect()
      resize.observe(strip)
      for (const child of Array.from(strip.children)) resize.observe(child)
    }
    observeAll()
    const mutations =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver((records) => {
            if (records.some((record) => record.type === 'childList')) observeAll()
            settle()
          })
    mutations?.observe(strip, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-current', 'aria-selected'] })

    strip.addEventListener('scroll', onScroll, { passive: true })
    // Not passive, and not React's `onWheel`, which React attaches as passive,
    // so it could not keep the page from scrolling as well.
    strip.addEventListener('wheel', onWheel, { passive: false })
    if (resize === null) window.addEventListener('resize', settle)
    const loop = hover.current

    return () => {
      strip.removeEventListener('scroll', onScroll)
      strip.removeEventListener('wheel', onWheel)
      if (resize === null) window.removeEventListener('resize', settle)
      resize?.disconnect()
      mutations?.disconnect()
      loop?.stop()
    }
  }, [scroll, measure])

  const step = useCallback((direction: -1 | 1) => {
    const strip = stripRef.current
    if (strip === null) return
    moved.current = true
    if (hover.current !== null && typeof performance !== 'undefined') hover.current.settleUntil = performance.now() + STEP_SETTLE_MS
    scrollStripTo(strip, stepTarget(metricsOf(strip), direction), true)
  }, [])

  if (!scroll) {
    return (
      <Tag ref={setRef} {...rest} className={className}>
        {children}
      </Tag>
    )
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const strip = stripRef.current
    const loop = hover.current
    if (strip === null || loop === null) return
    if (event.pointerType !== 'mouse') {
      loop.aim(0)
      return
    }
    const box = strip.getBoundingClientRect()
    loop.aim(hoverVelocity(event.clientX - box.left, box.width, stripEdges(metricsOf(strip))))
  }
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    moved.current = true
    if (event.pointerType === 'mouse' && hover.current !== null) hover.current.held = true
  }
  const release = () => {
    if (hover.current !== null) hover.current.held = false
  }
  const onPointerLeave = () => {
    release()
    hover.current?.aim(0)
  }

  return (
    <div
      className="relative min-w-0"
      onPointerMove={onPointerMove}
      onPointerDown={onPointerDown}
      onPointerUp={release}
      onPointerCancel={onPointerLeave}
      onPointerLeave={onPointerLeave}
    >
      <Tag ref={setRef} {...rest} className={className}>
        {children}
      </Tag>
      <StripEdgeControls edges={edges} onStep={step} />
    </div>
  )
})

/**
 * The arrows over the strip's clipped ends: one for each end with tabs hidden
 * beyond it, and nothing at all when everything fits.
 */
export function StripEdgeControls({ edges, onStep }: { edges: StripEdges; onStep: (direction: -1 | 1) => void }) {
  return (
    <>
      {edges.left ? <StripArrow side="left" onPress={() => onStep(-1)} /> : null}
      {edges.right ? <StripArrow side="right" onPress={() => onStep(1)} /> : null}
    </>
  )
}

/**
 * One end's arrow, on a solid backing so a tab's words do not show round it,
 * with a fade beside it over the clipped tabs.
 *
 * The overlay stops one pixel short of the bottom, so the strip's hairline, or
 * the bar's line under an `onBarEdge` strip, runs unbroken under it. Only the
 * button takes the pointer: a press on the fade lands on the tab under it.
 */
function StripArrow({ side, onPress }: { side: 'left' | 'right'; onPress: () => void }) {
  const left = side === 'left'
  return (
    <div
      className={cn('pointer-events-none absolute bottom-px top-0 flex items-stretch', left ? 'left-0' : 'right-0 flex-row-reverse')}
      // The same width as the hover zone, so the fade is exactly what scrolls when pointed at.
      style={{ width: EDGE_ZONE_PX }}
    >
      <div className={cn('flex items-center bg-surface', left ? 'pl-1' : 'pr-1')}>
        <button
          type="button"
          tabIndex={-1}
          aria-label={left ? 'Scroll tabs left' : 'Scroll tabs right'}
          onClick={onPress}
          className="pointer-events-auto grid h-7 w-7 place-items-center rounded-md text-ink-subtle transition-colors hover:bg-surface-hover hover:text-ink-secondary"
        >
          {left ? <IconChevronLeft className="h-4 w-4" /> : <IconChevronRight className="h-4 w-4" />}
        </button>
      </div>
      <div aria-hidden="true" className={cn('flex-1 from-surface to-transparent', left ? 'bg-gradient-to-r' : 'bg-gradient-to-l')} />
    </div>
  )
}
